import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Checkbox, IconButton, Table, Thead, Tbody, Tr, Th, Td, NumberInput, NumberInputField,
  Text, Spinner, Stack, Flex, useToast, Box, Select, useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { money } from '../utils/money'; // item 327 — stealth-aware
import useUsers from '../hooks/useUsers'; // item 348 — split picker roster
import useCommissionReasons from '../hooks/useCommissionReasons'; // item 1205 — ad-hoc earner reason
import useInvoice from '../hooks/useInvoice'; // item 1205b — the SKU picker reads the invoice lines
import { UserDto } from '../models/user';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 438 — one flagged line's contribution to a commission row (per-SKU drill-down)
interface PreviewLine {
  sku: string;
  brand: string;
  description: string;
  lineTotal: number;
  payout: number;
}

interface PreviewRow {
  booksOnFinalize?: boolean; // item 442/443 — false = nothing books at this finalize
  alreadyBooked?: boolean; // item 443 — this invoice already accrued (read-only rows)
  shareLabel?: string | null; // item 443 — e.g. "50% progress share"; null = full booking
  shareFraction?: number; // item 443 — this row's slice of its full amount (1 = full)
  earnedByUserId: string;
  name: string;
  commissionReasonId: string;
  reasonName: string;
  rate: number;
  basis: number;
  amount: number;
  lines?: PreviewLine[];
  // item 1205 — an AD-HOC earner added here carries an explicit dollar basis; sent to the
  // override save so the engine books rate × THIS basis (share-prorated like any row)
  customBasis?: number;
}

// item 341 — the ONE commission review surface, reused everywhere the split matters:
// the WO→invoice wizard (assign before the invoice is finalized), the Finalize modal
// (the double-check), the Adjust button, and — readOnly — on already-finalized
// invoices (view the booked split instead of a dead-end "already finalized" error).
export const CommissionReviewPanel: React.FC<{
  invoiceId: string;
  readOnly?: boolean;
  // item 442/443 — tells the host whether finalizing THIS invoice books commissions,
  // and (when partial) the progress-share label, e.g. "50% progress share"
  onBooksOnFinalize?: (books: boolean, shareLabel?: string | null) => void;
  // notifies the host when a save succeeds (wizard advances, modal closes, …)
  onSaved?: () => void;
  // render the save button inside the panel (standalone contexts); hosts with their
  // own footer (the modal) set this false and call save via the ref-less pattern below
  showSave?: boolean;
  // item 1206 (Chad: Tom's split never booked) — the host stores a "flush" here and calls
  // it right before finalizing: unsaved panel edits (rate changes, added earners) are
  // persisted as overrides so what the panel SHOWS is what BOOKS. Resolves false on failure.
  saveRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
}> = ({
  invoiceId, readOnly = false, onBooksOnFinalize, onSaved, showSave = true, saveRef,
}) => {
  const toast = useToast();
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [whyEmpty, setWhyEmpty] = useState<string[]>([]); // item 1470
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // item 1206 — any local edit (rate, split, added earner) marks the panel dirty; the
  // finalize flush only posts when there's actually something unsaved
  const dirtyRef = React.useRef(false);
  // item 348 — SPLIT: pick who + what %; a new row appears under the same reason.
  const { data: usersData } = useUsers();
  const roster = (usersData?.index ?? []).filter((u: UserDto) => !u.isArchive);
  const [splitFrom, setSplitFrom] = useState<number | null>(null);
  const [splitUserId, setSplitUserId] = useState('');
  const [splitRate, setSplitRate] = useState(0);
  // item 1205 (Chad: "add Tom for 5% of this dollar amount") — a brand-new earner row:
  // who + reason + an explicit $ basis + the TRUE rate; the invoice's progress share
  // applies automatically, exactly like the engine rows.
  const { reasons: reasonList } = useCommissionReasons();
  const [addOpen, setAddOpen] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addReasonId, setAddReasonId] = useState('');
  const [addBasis, setAddBasis] = useState(0);
  const [addRate, setAddRate] = useState(0);
  // item 1205b (Chad: "should we be able to choose from skus") — picking lines FILLS the
  // basis (the stored truth stays basis × rate); the dollar field remains editable after.
  const [addPicked, setAddPicked] = useState<Record<string, boolean>>({});
  const { data: invDetail } = useInvoice({ id: invoiceId });
  const invLines = ((invDetail as any)?.invoiceItems ?? []) as {
    id: string; sku?: string; description?: string; retailEach: number; quantity: number;
  }[];
  const lineTotal = (l: { retailEach: number; quantity: number }) => Math.round(l.retailEach * l.quantity * 100) / 100;
  const togglePickedLine = (id: string) => {
    setAddPicked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const sum = invLines.filter((l) => next[l.id]).reduce((a, l) => a + lineTotal(l), 0);
      setAddBasis(Math.round(sum * 100) / 100);
      return next;
    });
  };
  // item 438 — which rows are expanded to their per-SKU detail, keyed earner+reason.
  // item 445 — AUTO-EXPANDED once collapsed=false wins; null = "not user-touched yet"
  // so the load effect can default-open every row with detail.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const rowKey = (r: PreviewRow) => `${r.earnedByUserId}-${r.commissionReasonId}`;
  const toggleExpanded = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  // item 445 — the rate field must be visible no matter how the theme variant resolves
  // (variant="filled" on NumberInputField was ignored → white-on-white). Explicit.
  const fieldBg = useColorModeValue('white', 'whiteAlpha.200');
  const fieldText = useColorModeValue('gray.900', 'white');
  const fieldBorder = useColorModeValue('gray.300', 'whiteAlpha.400');

  const applySplit = () => {
    if (splitFrom === null || !splitUserId || splitRate <= 0) return;
    const src = rows[splitFrom];
    const partner = roster.find((u: UserDto) => u.id === splitUserId);
    if (!src || !partner) return;
    dirtyRef.current = true; // item 1206
    setRows((prev) => {
      const srcShare = src.shareFraction ?? 1; // item 443 — slices stay slices through a split
      const next = prev.map((r, idx) => (idx === splitFrom
        ? { ...r, rate: Math.max(0, Math.round((r.rate - splitRate) * 100) / 100), amount: Math.round(r.basis * Math.max(0, r.rate - splitRate) * srcShare) / 100 }
        : r));
      next.splice(splitFrom + 1, 0, {
        earnedByUserId: partner.id,
        name: partner.name,
        commissionReasonId: src.commissionReasonId,
        reasonName: src.reasonName,
        rate: splitRate,
        basis: src.basis,
        amount: Math.round(src.basis * splitRate * srcShare) / 100,
        shareFraction: src.shareFraction,
        lines: src.lines, // item 438 — same reason, same contributing SKUs
      });
      return next;
    });
    setSplitFrom(null);
    setSplitUserId('');
    setSplitRate(0);
  };

  // item 1373 (Chad, INV-100599: "we added an earner at finalize, we saved split… it booked
  // jake, jason, and dustin, but not the added earner") — the add-earner card was a TWO-step
  // form that read as one: fill it in, then press the card's own "Add earner" to put the row
  // in the split, THEN Save split / finalize. Chad filled it in and went straight to Save
  // split → the three rows already in the table posted, the filled-in card was silently
  // discarded, and Tom Blanchard's 5% never booked. A filled-in, VALID card is intent: both
  // Save split and the pre-finalize flush now fold it into the split automatically.
  const pendingRow = (): PreviewRow | null => {
    if (!addOpen || !addUserId || !addReasonId || addBasis <= 0 || addRate <= 0) return null;
    const person = roster.find((u: UserDto) => u.id === addUserId);
    const reason = reasonList.find((r) => r.id === addReasonId);
    if (!person || !reason) return null;
    const share = rows[0]?.shareFraction ?? 1; // slices stay slices, like Split does
    return {
      earnedByUserId: person.id,
      name: person.name,
      commissionReasonId: reason.id,
      reasonName: reason.name,
      rate: addRate,
      basis: addBasis,
      customBasis: addBasis,
      amount: Math.round(addBasis * addRate * share) / 100,
      shareFraction: share,
      lines: [],
    };
  };
  const resetAddForm = () => {
    setAddOpen(false);
    setAddUserId('');
    setAddReasonId('');
    setAddBasis(0);
    setAddRate(0);
    setAddPicked({});
  };
  // the rows that WILL post: the table plus a valid, not-yet-added card
  const rowsToPost = (): PreviewRow[] => {
    const p = pendingRow();
    return p ? [...rows, p] : rows;
  };

  const applyAddEarner = () => {
    const p = pendingRow();
    if (!p) return;
    dirtyRef.current = true; // item 1206
    setRows((prev) => ([...prev, p]));
    resetAddForm();
  };

  useEffect(() => {
    if (!invoiceId) return undefined;
    let alive = true;
    setLoading(true);
    axios.get(`${apiUrl()}Commission/Preview/${invoiceId}`)
      .then(({ data }) => {
        if (!alive) return;
        const list = (Array.isArray(data) ? data : []) as PreviewRow[];
        setRows(list);
        dirtyRef.current = false; // item 1206 — fresh server truth
        // item 445 — auto-expand every row that has per-SKU detail, so the split is
        // visible at a glance without clicking each chevron (Chad)
        setExpanded(Object.fromEntries(
          list.filter((r) => (r.lines?.length ?? 0) > 0).map((r) => [rowKey(r), true]),
        ));
        // item 442/443 — surface the booking gate + progress share to the host modal
        const first = list[0];
        if (first !== undefined) onBooksOnFinalize?.(first.booksOnFinalize !== false, first.shareLabel ?? null);
        // item 1470 (Chad, Graton PROP-100677 booked nothing and nobody knew why) — an EMPTY
        // projection used to leave the host thinking it books; now it is told NO and asked WHY.
        if (list.length === 0) {
          onBooksOnFinalize?.(false, null);
          axios.get(`${apiUrl()}Commission/Preview/${invoiceId}/why`)
            .then(({ data: w }) => { if (alive) setWhyEmpty(Array.isArray(w?.reasons) ? w.reasons : []); })
            .catch(() => { if (alive) setWhyEmpty([]); });
        } else {
          setWhyEmpty([]);
        }
      })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // only refetch the preview when the invoice changes — onBooksOnFinalize is a callback prop
    // and adding it would re-fetch on every parent render (dart 2026-07-14).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  // item 443 — a progress invoice books a SLICE: live recomputes scale by the row's
  // shareFraction so the panel keeps matching what will actually book
  const setRate = (i: number, rate: number) => {
    dirtyRef.current = true; // item 1206
    setRows((prev) => prev.map((r, idx) => (idx === i
      ? { ...r, rate, amount: Math.round(r.basis * rate * (r.shareFraction ?? 1)) / 100 }
      : r)));
  };

  const postOverrides = React.useCallback(async (rowList: PreviewRow[]) => {
    await axios.post(`${apiUrl()}Commission/Override/${invoiceId}`, {
      // item 1334 (Chad: "fix this bug once and for all") — a 0% row IS the user's intent
      // ("this person earns NOTHING on this invoice") and MUST be posted. This filter used
      // to drop rate<=0, so "zero everyone + Save split" posted an EMPTY set — the server
      // (fixed for zeros in 1117) then CLEARED the overrides, and the engine read "no
      // override given" and booked everyone's standing rate anyway (INV-100547). Only
      // genuinely invalid rows (negative) are dropped; the server refuses empty sets now.
      rows: rowList
        .filter((r) => r.rate >= 0)
        .map((r) => ({
          earnedByUserId: r.earnedByUserId,
          commissionReasonId: r.commissionReasonId,
          rate: r.rate,
          basis: r.customBasis, // item 1205 — only ad-hoc rows carry a dollar basis
        })),
    });
    dirtyRef.current = false;
  }, [invoiceId]);

  const save = async () => {
    setSaving(true);
    try {
      const list = rowsToPost(); // item 1373 — a filled-in add-earner card rides along
      if (list.length !== rows.length) { setRows(list); resetAddForm(); }
      await postOverrides(list);
      toast({ status: 'success', title: 'Commission split saved', description: 'It will apply when this invoice is finalized.' });
      onSaved?.();
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not save', description: e?.response?.data ?? 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  // item 1206 — the host's pre-finalize flush: persist unsaved edits so WYSIWYG holds.
  // No-op when nothing changed (an untouched panel keeps pure engine behavior — no
  // override rows are written, so standing rates stay live).
  React.useEffect(() => {
    if (!saveRef) return undefined;
    saveRef.current = async () => {
      const list = rowsToPost(); // item 1373 — a filled-in add-earner card IS an edit
      const hasPending = list.length !== rows.length;
      if (!dirtyRef.current && !hasPending) return true;
      try {
        if (hasPending) { setRows(list); resetAddForm(); }
        await postOverrides(list);
        return true;
      } catch (e: any) {
        toast({ status: 'error', title: 'Could not save the commission split', description: e?.response?.data ?? 'Fix it and finalize again.' });
        return false;
      }
    };
    return () => { saveRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRef, rows, postOverrides, toast, addOpen, addUserId, addReasonId, addBasis, addRate]);

  const total = rows.reduce((sum, r) => sum + r.amount, 0);

  // item 759 — defense regardless of host: when every row came back BOOKED, the split is
  // locked — treat as read-only even if the host forgot to say so.
  const ro = readOnly || (rows.length > 0 && rows.every((r) => r.alreadyBooked));

  if (loading) return <Flex justify="center" py={8}><Spinner /></Flex>;
  return (
    <Box>
      {rows.length === 0 && whyEmpty.length === 0 && (
        <Text fontSize="sm" py={2}>
          No commissions project for this invoice — flag the items with a commission reason and
          set per-user %s in Settings, or add an earner manually below.
        </Text>
      )}
      {/* item 1470 — the invoice HAS flagged lines but every reason resolves to nobody: say why, loudly */}
      {rows.length === 0 && whyEmpty.length > 0 && (
        <Box borderWidth="1px" borderColor="red.400" bg="rgba(229,62,62,0.08)" borderRadius="md" p={3} mb={3}>
          <Text fontSize="sm" fontWeight="bold" color="red.300" mb={1}>This invoice will book NO commissions</Text>
          {whyEmpty.map((w) => <Text key={w} fontSize="sm">{`• ${w}`}</Text>)}
          <Text fontSize="xs" color="gray.400" mt={2}>
            Fix the cause (assign the lead on the work order, set the customer&apos;s rep in CRM, link the PM) and reopen this step — or add an earner manually below.
          </Text>
        </Box>
      )}
      {ro && (
        <Text fontSize="sm" color="orange.400" mb={3}>
          This invoice is finalized — the split below is BOOKED and locked. To change it, void
          the invoice (the work order reopens) and regenerate; you can adjust before the new
          finalize.
        </Text>
      )}
      {/* item 1063 — the 5-column adjust table scrolls inside the modal on tablet */}
      <Box overflowX="auto">
        <Table size="sm" minW="640px">
          <Thead>
            <Tr>
              <Th>Earner</Th>
              <Th>Reason</Th>
              <Th isNumeric>Basis</Th>
              <Th isNumeric>Rate %</Th>
              <Th isNumeric>Payout</Th>
              {!ro && <Th />}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r, i) => (
              <React.Fragment key={rowKey(r)}>
                <Tr>
                  <Td>
                    <Flex align="center" gap={1}>
                      {/* item 438 — expand to the per-SKU contributions */}
                      {(r.lines?.length ?? 0) > 0 && (
                      <IconButton
                        aria-label={expanded[rowKey(r)] ? 'Hide SKU detail' : 'Show SKU detail'}
                        icon={expanded[rowKey(r)] ? <ChevronDownIcon /> : <ChevronRightIcon />}
                        size="xs"
                        variant="ghost"
                        onClick={() => toggleExpanded(rowKey(r))}
                      />
                      )}
                      <Text as="span">{r.name}</Text>
                    </Flex>
                  </Td>
                  <Td>{r.reasonName}</Td>
                  <Td isNumeric>{money(r.basis)}</Td>
                  <Td isNumeric>
                    {ro ? (
                      <Text as="span">{`${r.rate}%`}</Text>
                    ) : (
                      <NumberInput
                        size="sm"
                        maxW="90px"
                        ml="auto"
                        value={r.rate}
                        min={0}
                        max={100}
                        precision={2}
                        onChange={(_, n) => setRate(i, Number.isNaN(n) ? 0 : n)}
                      >
                        {/* item 437/445 — the variant prop was ignored on NumberInputField
                        (Chakra 1.6 reads it from context), so the box stayed white-on-
                        white. Explicit bg/color/border guarantees the rate is visible. */}
                        <NumberInputField
                          textAlign="right"
                          bg={fieldBg}
                          color={fieldText}
                          borderColor={fieldBorder}
                        />
                      </NumberInput>
                    )}
                  </Td>
                  <Td isNumeric>{money(r.amount)}</Td>
                  {!ro && (
                  <Td px={1}>
                    <Button size="xs" variant="ghost" onClick={() => { setSplitFrom(i); setSplitUserId(''); setSplitRate(0); }}>
                      Split
                    </Button>
                  </Td>
                  )}
                </Tr>
                {/* item 438 — the per-SKU drill-down: line value @ the CURRENT rate, so
                rate edits live-update these figures too */}
                {expanded[rowKey(r)] && (r.lines?.length ?? 0) > 0 && (
                <Tr>
                  <Td colSpan={ro ? 5 : 6} pt={0} pb={3} borderBottomWidth={0}>
                    <Box pl={9}>
                      {(r.lines ?? []).map((l, li) => (
                      // eslint-disable-next-line react/no-array-index-key
                        <Flex key={`${l.sku}-${li}`} justify="space-between" align="baseline" gap={3} py={0.5}>
                          <Box minW={0}>
                            <Text fontSize="xs" fontWeight="semibold" as="span">{l.sku || '—'}</Text>
                            {l.brand && <Text fontSize="xs" color="gray.500" as="span">{` · ${l.brand}`}</Text>}
                            {l.description && (
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>{l.description}</Text>
                            )}
                          </Box>
                          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                            {`${money(l.lineTotal)} @ ${r.rate}%${(r.shareFraction ?? 1) < 1 ? ` × ${Math.round((r.shareFraction ?? 1) * 100)}%` : ''} = `}
                            <Text as="span" fontWeight="semibold">{money(Math.round(l.lineTotal * r.rate * (r.shareFraction ?? 1)) / 100)}</Text>
                          </Text>
                        </Flex>
                      ))}
                    </Box>
                  </Td>
                </Tr>
                )}
              </React.Fragment>
            ))}
            {/* item 348 — the split mini-form: with WHO, and what the split is */}
            {!ro && splitFrom !== null && rows[splitFrom] && (
            <Tr>
              <Td colSpan={6}>
                <Flex gap={2} align="center" wrap="wrap">
                  <Text fontSize="sm">{`Split ${rows[splitFrom].name}'s ${rows[splitFrom].reasonName} with`}</Text>
                  <Select size="sm" maxW="220px" placeholder="— choose who —" value={splitUserId} onChange={(e) => setSplitUserId(e.target.value)}>
                    {roster.filter((u: UserDto) => u.id !== rows[splitFrom]!.earnedByUserId).map((u: UserDto) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </Select>
                  <NumberInput size="sm" maxW="100px" min={0.25} max={rows[splitFrom].rate} precision={2} value={splitRate || ''} onChange={(_, n) => setSplitRate(Number.isNaN(n) ? 0 : n)}>
                    <NumberInputField variant="filled" placeholder="% of basis" textAlign="right" />
                  </NumberInput>
                  <Button size="sm" isDisabled={!splitUserId || splitRate <= 0} onClick={applySplit}>Confirm split</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSplitFrom(null)}>Cancel</Button>
                </Flex>
              </Td>
            </Tr>
            )}
            <Tr>
              <Td colSpan={ro ? 4 : 5} textAlign="right"><Text fontSize="sm" fontWeight="bold">Total commissions</Text></Td>
              <Td isNumeric><Text fontWeight="bold">{money(total)}</Text></Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
      {/* item 1205/1205b (Chad: "ugly") — the add-earner form is a proper labeled card
          below the table, with a SKU picker that FILLS the basis. Enter the TRUE rate;
          the invoice's progress share applies automatically like every engine row. */}
      {!ro && addOpen && (
        <Box mt={3} p={4} borderWidth="1px" borderRadius="lg">
          <Text fontSize="sm" fontWeight="semibold" mb={3}>Add an earner to this invoice</Text>
          <Flex gap={3} wrap="wrap" align="flex-end">
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Earner</Text>
              <Select size="sm" minW="180px" placeholder="— choose —" value={addUserId} onChange={(e) => setAddUserId(e.target.value)}>
                {roster.map((u: UserDto) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Reason</Text>
              <Select size="sm" minW="160px" placeholder="— choose —" value={addReasonId} onChange={(e) => setAddReasonId(e.target.value)}>
                {reasonList.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Basis $</Text>
              <NumberInput size="sm" maxW="130px" min={0} precision={2} value={addBasis || ''} onChange={(_, n) => setAddBasis(Number.isNaN(n) ? 0 : n)}>
                <NumberInputField placeholder="0.00" textAlign="right" bg={fieldBg} color={fieldText} borderColor={fieldBorder} />
              </NumberInput>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Rate % (true rate)</Text>
              <NumberInput size="sm" maxW="100px" min={0} max={100} precision={2} value={addRate || ''} onChange={(_, n) => setAddRate(Number.isNaN(n) ? 0 : n)}>
                <NumberInputField placeholder="0" textAlign="right" bg={fieldBg} color={fieldText} borderColor={fieldBorder} />
              </NumberInput>
            </Box>
          </Flex>
          {invLines.length > 0 && (
            <Box mt={3}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Or pick the SKUs it pays on — they fill the basis (still editable):
              </Text>
              <Stack spacing={0.5} maxH="140px" overflowY="auto" pr={2}>
                {invLines.map((l) => (
                  <Checkbox key={l.id} size="sm" isChecked={!!addPicked[l.id]} onChange={() => togglePickedLine(l.id)}>
                    <Text as="span" fontSize="xs">
                      {`${l.sku || l.description || 'line'} — ${money(lineTotal(l))}`}
                    </Text>
                  </Checkbox>
                ))}
              </Stack>
            </Box>
          )}
          <Flex justify="space-between" align="center" mt={3} wrap="wrap" gap={2}>
            <Text fontSize="sm" color="gray.500">
              {addBasis > 0 && addRate > 0 ? (
                <>
                  {`${money(addBasis)} @ ${addRate}%${(rows[0]?.shareFraction ?? 1) < 1 ? ` × ${Math.round((rows[0]?.shareFraction ?? 1) * 100)}%` : ''} = `}
                  <Text as="span" fontWeight="bold">{money(Math.round(addBasis * addRate * (rows[0]?.shareFraction ?? 1)) / 100)}</Text>
                  {' books at finalize — it is included even if you don\'t press Add to split'}
                </>
              ) : 'Set a basis and rate to see the payout.'}
            </Text>
            <Flex gap={2}>
              <Button size="sm" variant="ghost" onClick={() => { setAddOpen(false); setAddPicked({}); }}>Cancel</Button>
              <Button size="sm" isDisabled={!pendingRow()} onClick={applyAddEarner}>
                Add to split
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}
      {!ro && (
        <Flex justify="space-between" align="center" mt={3}>
          <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} isDisabled={addOpen}>
            + Add earner
          </Button>
          {showSave && <Button size="sm" isLoading={saving} onClick={save}>Save split</Button>}
        </Flex>
      )}
    </Box>
  );
};

CommissionReviewPanel.defaultProps = {
  saveRef: undefined,
  readOnly: false,
  onBooksOnFinalize: undefined,
  onSaved: undefined,
  showSave: true,
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  // item 341(c) — finalized invoice: show the booked split read-only instead of a
  // dead-end "already finalized" error on save
  locked?: boolean;
}

// item 317/341 — adjust the per-invoice commission split before finalizing (or VIEW the
// booked split after). The panel is shared with the WO→invoice wizard + Finalize modal.
const CommissionAdjustModal: React.FC<Props> = ({
  isOpen, onClose, invoiceId, locked = false,
}) => (
  <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
    <ModalOverlay />
    <ModalContent>
      <ModalHeader>{locked ? 'Booked commissions' : 'Adjust commissions'}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {!locked && (
          <Text fontSize="sm" color="gray.500" mb={3}>
            These are the commissions that will be booked when this invoice is finalized. Edit a
            rate to override the split for this invoice only — everyone&apos;s default % stays
            unchanged. Clear a rate to 0 to drop that payout.
          </Text>
        )}
        {isOpen && <CommissionReviewPanel invoiceId={invoiceId} readOnly={locked} onSaved={onClose} />}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>{locked ? 'Close' : 'Cancel'}</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

CommissionAdjustModal.defaultProps = { locked: false };

export default CommissionAdjustModal;
